import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Store } from '@ngxs/store';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Posting } from '../../models/posting.model';
import { AllPostingsState } from '../../states/all-postings.state';
import { PostingsDataSource } from './datasource';

@Component({
    selector: 'app-select-postings-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SelectPostingsListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    postings$: Observable<Posting[]>;

    @Input()
    postingsStateSearchTerm$: Observable<string>;
    postingsStateSearchTerm: string;

    @Input()
    postingsStateTotalCount$: Observable<number>;

    @Input()
    loadPostings$: Observable<boolean>;

    @Input()
    selectedPostingTypeId: number;

    @Input()
    selectedContentTypeId: number;

    @Input()
    onlyValid = false;

    @Input()
    selectedLanguageId: string;

    @Input()
    selectedPostingsList: Posting[] = [];

    @Input()
    singleSelect = false;

    @Output()
    updateList = new EventEmitter<Posting[]>();

    @Input()
    removePostingFromList$: Observable<Posting>;

    dataSource: PostingsDataSource;
    displayedColumns = ['avatar', 'name', 'validFrom', 'validTo', 'buttons'];

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;
    @ViewChild('chipList')
    chipList: ElementRef;

    constructor(private _store: Store, private _action$: Actions) {
        super();
    }

    ngOnInit(): void {
        this.dataSource = new PostingsDataSource(this.postings$, this._store, this._action$);

        if (!this.singleSelect) {
            this.displayedColumns = ['avatar', 'name', 'type', 'validFrom', 'validTo', 'buttons'];
        }
    }

    ngAfterViewInit(): void {
        this.postingsStateSearchTerm$
            .pipe(
                filter((searchTerm, index) => {
                    this.postingsStateSearchTerm = searchTerm || '';
                    return index > 0;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.loadPostingsPage(true);
            });

        this.removePostingFromList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((action: Posting | { clear: true }) => {
            if (!action) return;

            if ('clear' in action) {
                this.selectedPostingsList = [];

                this.dataSource = new PostingsDataSource(
                    this.postings$.pipe(map((modules) => modules.map((item) => Object.assign(new Posting(), item, { selected: false })))),
                    this._store,
                    this._action$
                );

                this.updateList.emit([]);
            } else {
                this.removeFromList(action);
            }
        });

        this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
            const container2 = document.getElementById('container-2');
            const container3 = document.getElementById('container-3');
            if (!container2 || !container3) {
                return;
            }

            const itemsleft = length - pageIndex * pageSize;
            const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
            // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
            const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
            const contentElementHeight = container2.offsetHeight;
            const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

            setTimeout(() => {
                container3.scrollTop = scrollTop;
            });
        });

        this.loadPostingsPage(false);

        this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
            this.loadPostingsPage(true);
        });

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadPostingsPage(false);
        });

        this.loadPostings$
            .pipe(
                filter((resetPageIndex) => resetPageIndex !== null),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((resetPageIndex) => {
                this.loadPostingsPage(resetPageIndex);
            });
    }

    removeFromList(module: Posting): void {
        this.selectedPostingsList = [...this.selectedPostingsList.filter((item) => item.id !== module.id)];

        this.dataSource = new PostingsDataSource(
            this.postings$.pipe(
                map((modules) => {
                    return modules.map((item) => {
                        if (item.id === module.id) {
                            item = Object.assign(new Posting(), item, {
                                selected: false,
                            });
                        } else if (this.selectedPostingsList.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new Posting(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );

        this.updateList.emit(this.selectedPostingsList);
    }

    addToList(user: Posting): void {
        this.selectedPostingsList = this.singleSelect ? [user] : [...this.selectedPostingsList, user];
        this.dataSource = new PostingsDataSource(
            this.postings$.pipe(
                map((modules) => {
                    return modules.map((item) => {
                        if (item.id === user.id || this.selectedPostingsList.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new Posting(), item, {
                                selected: true,
                            });
                        } else if (this.singleSelect) {
                            item = Object.assign(new Posting(), item, {
                                selected: false,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
        this.updateList.emit(this.selectedPostingsList);
    }

    loadPostingsPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        const searchTerm = this._store.selectSnapshot(AllPostingsState.getSearchTerm) || this.postingsStateSearchTerm || '';

        this.dataSource.loadPostings(
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            this.sort.active,
            this.sort.direction,
            searchTerm,
            this.selectedPostingTypeId,
            this.selectedLanguageId,
            this.selectedContentTypeId,
            this.onlyValid
        );
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }
}
